import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppBody } from "@/components/layout/AppBody";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { PROFILE_TABS } from "@/components/shared/candidate/CandidateProfile";
import type { Tab as ProfileTab } from "@/components/shared/candidate/CandidateProfile";
import type { ScreeningJob, ScreenBody } from "@/components/pages/screen/types";
import { api, DuplicateError } from "@/lib/api";
import { extractTextFromFile } from "@/lib/extractText";
import { getPendingResume, clearPendingResume } from "@/lib/pendingResume";
import type { AuthUser, HiringMatrix, Position } from "@/types";
import { canSeeTab, hasPermission } from "@/types";

const DIALOG_ROUTES = ["cv", "interview", "endorse"] as const;
type ProfileDialog = typeof DIALOG_ROUTES[number];

interface AppRoutingProps {
  authUser: AuthUser;
  sidebarOpen: boolean;
  onSidebarClose: () => void;
  onProfileUpdate: (updates: Partial<AuthUser>) => void;
}

export function AppRouting({ authUser, sidebarOpen, onSidebarClose, onProfileUpdate }: AppRoutingProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const profileCandidateId = pathSegments[0] === "candidates" && pathSegments[1] === "profile" && pathSegments[2]
    ? (parseInt(pathSegments[2], 10) || null)
    : null;
  const rawProfileSegment = pathSegments[3];
  const profileDialog: ProfileDialog | null = (DIALOG_ROUTES as readonly string[]).includes(rawProfileSegment ?? "")
    ? (rawProfileSegment as ProfileDialog)
    : null;
  const profileTab: ProfileTab = profileDialog
    ? "personal"
    : (PROFILE_TABS as string[]).includes(rawProfileSegment ?? "")
      ? (rawProfileSegment as ProfileTab)
      : "personal";
  const orgAnalyticsId = pathSegments[0] === "organizations" && pathSegments[2] === "analytics"
    ? (parseInt(pathSegments[1], 10) || null)
    : null;
  const page = profileCandidateId != null ? "candidate-profile"
    : orgAnalyticsId != null ? "org-analytics"
    : (pathSegments[0] as string) || "candidates";

  const effectivePage = page === "settings" ? "settings"
    : page === "candidate-profile" ? "candidate-profile"
    : page === "org-analytics" ? "org-analytics"
    : page === "organizations" && canSeeTab(authUser, "organizations") ? "organizations"
    : page === "roles" && (canSeeTab(authUser, "roles") || (authUser.role === "admin" && !!authUser.org_id)) ? "roles"
    : page === "permissions" && hasPermission(authUser, "roles:manage") ? "permissions"
    : page === "pipeline" && hasPermission(authUser, "candidates:read") ? "pipeline"
    : canSeeTab(authUser, page) ? page
    : canSeeTab(authUser, "candidates") ? "candidates"
    : canSeeTab(authUser, "screen") ? "screen"
    : "positions";

  const [positions, setPositions] = useState<Position[]>([]);
  const [matrices, setMatrices] = useState<HiringMatrix[]>([]);
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  const [selectedMatrixId, setSelectedMatrixId] = useState<number | null>(null);
  const [jobs, setJobs] = useState<ScreeningJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCandidateCount, setNewCandidateCount] = useState(0);
  const [usePipeline, setUsePipeline] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(() => {
    const f = getPendingResume();
    if (f) clearPendingResume();
    return f;
  });

  const refreshPositions = async () => {
    const { positions: pos } = await api.listPositions();
    setPositions(pos);
    return pos;
  };

  const refreshMatrices = async (posId?: number) => {
    const { matrices: mats } = await api.listMatrices(posId);
    setMatrices(mats);
    return mats;
  };

  useEffect(() => {
    (async () => {
      try {
        const [pos, { matrices: allMats }] = await Promise.all([
          refreshPositions(),
          api.listMatrices(),
        ]);
        if (pos.length > 0) {
          if (allMats.length > 0) {
            // Pick the first position that has matrices so the Screen page starts ready
            const firstPosId = allMats[0].position_id;
            const positionMats = allMats.filter((m) => m.position_id === firstPosId);
            setSelectedPositionId(firstPosId);
            setMatrices(positionMats);
            setSelectedMatrixId(positionMats[0].id);
          } else {
            setSelectedPositionId(pos[0].id);
          }
        }
      } catch {
        // ignore
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (selectedPositionId == null) {
      setMatrices([]);
      setSelectedMatrixId(null);
      return;
    }
    (async () => {
      const mats = await refreshMatrices(selectedPositionId);
      setSelectedMatrixId(mats.length > 0 ? mats[0].id : null);
    })();
  }, [selectedPositionId]);

  useEffect(() => {
    if (profileCandidateId && !rawProfileSegment) {
      navigate(`/candidates/profile/${profileCandidateId}/personal`, { replace: true });
    }
  }, [profileCandidateId, rawProfileSegment, navigate]);

  const handlePositionsRefresh = async () => {
    const pos = await refreshPositions();
    if (selectedPositionId && !pos.find((p) => p.id === selectedPositionId)) {
      setSelectedPositionId(pos[0]?.id ?? null);
    }
  };

  const updateJob = (jobId: string, updates: Partial<ScreeningJob>) =>
    setJobs((js) => js.map((j) => j.id === jobId ? { ...j, ...updates } : j));

  const screenOneFile = async (file: File): Promise<boolean> => {
    const jobId = crypto.randomUUID();
    setJobs((js) => [{ id: jobId, filename: file.name, status: "extracting" }, ...js]);
    let capturedBody: ScreenBody | null = null;
    try {
      const result = await extractTextFromFile(file, (detail) => {
        const isOcr = detail.toLowerCase().includes("ocr") || detail.toLowerCase().includes("image");
        updateJob(jobId, { status: isOcr ? "ocr" : "extracting", statusDetail: detail });
      });
      updateJob(jobId, { status: "screening", statusDetail: undefined });
      capturedBody = { position_id: selectedPositionId!, matrix_id: selectedMatrixId!, filename: file.name, resume_text: result.text, photo: result.photo, resume_file_base64: result.fileBase64, resume_file_mime: result.fileMime };
      const { candidate } = usePipeline ? await api.runPipeline(capturedBody) : await api.screen(capturedBody);
      updateJob(jobId, { status: "done", candidate });
      setNewCandidateCount((c) => c + 1);
      setTimeout(() => setJobs((js) => js.filter((j) => j.id !== jobId)), 3000);
      return true;
    } catch (e) {
      if (e instanceof DuplicateError) {
        updateJob(jobId, { status: "duplicate", existingCandidate: e.existingCandidate, pendingBody: capturedBody ?? undefined });
      } else {
        updateJob(jobId, { status: "error", error: (e as Error).message });
      }
      return false;
    }
  };

  const handleFiles = async (files: File[]) => {
    if (!selectedPositionId || !selectedMatrixId) {
      alert("Select a position and hiring matrix first.");
      return;
    }
    let hasSuccess = false;
    for (const file of files) {
      if (await screenOneFile(file)) hasSuccess = true;
    }
    if (hasSuccess) navigate("/candidates");
  };

  const handleForceScreen = async (job: ScreeningJob) => {
    if (!job.pendingBody) return;
    const { id: jobId, pendingBody } = job;
    updateJob(jobId, { status: "screening", existingCandidate: undefined, pendingBody: undefined });
    try {
      const { candidate } = await api.screen({ ...pendingBody, force: true });
      updateJob(jobId, { status: "done", candidate });
      setNewCandidateCount((c) => c + 1);
      setTimeout(() => setJobs((js) => js.filter((j) => j.id !== jobId)), 3000);
      navigate("/candidates");
    } catch (e) {
      updateJob(jobId, { status: "error", error: (e as Error).message });
    }
  };

  return (
    <div className="relative flex flex-1 overflow-hidden">
      <AppSidebar
        authUser={authUser}
        open={sidebarOpen}
        effectivePage={effectivePage}
        newCandidateCount={newCandidateCount}
        onClearCandidateBadge={() => setNewCandidateCount(0)}
        onClose={onSidebarClose}
      />
      <AppBody
        loading={loading}
        effectivePage={effectivePage}
        authUser={authUser}
        positions={positions}
        matrices={matrices}
        selectedPositionId={selectedPositionId}
        selectedMatrixId={selectedMatrixId}
        selectedMatrix={matrices.find((m) => m.id === selectedMatrixId) ?? null}
        jobs={jobs}
        usePipeline={usePipeline}
        profileCandidateId={profileCandidateId}
        profileTab={profileTab}
        profileDialog={profileDialog}
        orgAnalyticsId={orgAnalyticsId}
        onPositionChange={setSelectedPositionId}
        onMatrixChange={setSelectedMatrixId}
        onFiles={handleFiles}
        onClearJobs={() => setJobs([])}
        onGoToMatrices={() => navigate("/matrices")}
        pendingFile={pendingFile}
        onClearPendingFile={() => setPendingFile(null)}
        onTogglePipeline={setUsePipeline}
        onForceScreen={handleForceScreen}
        onViewCandidate={(id) => navigate(`/candidates/${id}`)}
        onPositionsChange={handlePositionsRefresh}
        onMatricesChange={handlePositionsRefresh}
        onViewProfile={(id) => navigate(`/candidates/profile/${id}/personal`)}
        onProfileTabChange={(tab) => navigate(`/candidates/profile/${profileCandidateId}/${tab}`)}
        onProfileBack={() => navigate("/candidates")}
        onProfileDialogClose={() => navigate(`/candidates/profile/${profileCandidateId}/personal`)}
        onProfileOpenDialog={(dialog) => navigate(`/candidates/profile/${profileCandidateId}/${dialog}`)}
        onOrgAnalyticsBack={() => navigate("/organizations")}
        onProfileUpdate={onProfileUpdate}
      />
    </div>
  );
}
